import React, { useEffect, useMemo, useState } from "react";
import { PageHeader, PageBody } from "@/components/PageChrome";
import { api, downloadFile, formatApiError } from "@/lib/api";
import MultiSelectFilter from "@/components/MultiSelectFilter";
import ImageThumb from "@/components/ImageThumb";
import { toast } from "sonner";
import {
  ShoppingCart, Truck, XCircle, CheckCircle2, RotateCcw, Package,
  FileText, ArrowLeftRight, Undo2, Boxes, Barcode,
  TrendingUp, TrendingDown, Minus, Download,
} from "lucide-react";

/**
 * Dashboard — 7 sections, per-section date ranges, global account+marketplace
 * filters, sparklines under every stat card, optional "vs previous period"
 * delta arrows. Feb 28 2026.
 */

// ── shared UI ───────────────────────────────────────────────────────────────
const INR = new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 });
const fmtAmt = (n) => `₹${INR.format(Math.round(Number(n) || 0))}`;
const fmtCount = (n) => INR.format(Math.round(Number(n) || 0));

/** Compute the % delta between current and previous, capped for display. */
function computeDelta(cur, prev) {
  const c = Number(cur) || 0;
  const p = Number(prev) || 0;
  if (p === 0) return c === 0 ? null : { pct: null, up: c > 0, flat: false };
  const pct = ((c - p) / Math.abs(p)) * 100;
  return { pct, up: pct > 0, flat: Math.abs(pct) < 0.5 };
}

function DeltaBadge({ cur, prev, testId }) {
  const d = computeDelta(cur, prev);
  if (d === null) {
    return <span className="text-[10px] text-slate-400 italic" data-testid={testId}>no prior data</span>;
  }
  const Icon = d.flat ? Minus : (d.up ? TrendingUp : TrendingDown);
  const tone = d.flat ? "text-slate-500" : (d.up ? "text-emerald-600" : "text-rose-600");
  const arrow = d.pct === null ? "new" : `${d.up ? "+" : ""}${d.pct.toFixed(1)}%`;
  return (
    <span className={`inline-flex items-center gap-1 text-[10.5px] font-semibold ${tone}`} data-testid={testId}>
      <Icon size={11} /> {arrow} <span className="text-slate-400 font-normal">vs prev</span>
    </span>
  );
}

/** Tiny inline SVG sparkline — no library. Shows 30 daily counts. */
function Sparkline({ data, color = "#334155", testId }) {
  if (!Array.isArray(data) || data.length === 0) {
    return <div className="h-6 mt-2" data-testid={testId} />;
  }
  const w = 100, h = 24;
  const max = Math.max(1, ...data);
  const step = w / (data.length - 1 || 1);
  const points = data.map((v, i) => `${(i * step).toFixed(2)},${(h - (v / max) * h).toFixed(2)}`).join(" ");
  const areaPoints = `0,${h} ${points} ${w},${h}`;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" className="w-full h-7 mt-2 block" data-testid={testId}>
      <polygon points={areaPoints} fill={color} opacity="0.15" />
      <polyline points={points} fill="none" stroke={color} strokeWidth="1.4" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}

function DateRange({ from, to, onChange, testIdPrefix }) {
  return (
    <div className="inline-flex items-center gap-2 text-[12px]">
      <span className="text-slate-500 font-medium">From</span>
      <input
        type="date" value={from || ""}
        onChange={(e) => onChange({ from: e.target.value, to })}
        data-testid={`${testIdPrefix}-from`}
        className="h-8 px-2 border border-slate-200 rounded-md bg-white font-mono text-[12px] focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400"
      />
      <span className="text-slate-500 font-medium">To</span>
      <input
        type="date" value={to || ""}
        onChange={(e) => onChange({ from, to: e.target.value })}
        data-testid={`${testIdPrefix}-to`}
        className="h-8 px-2 border border-slate-200 rounded-md bg-white font-mono text-[12px] focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400"
      />
      {(from || to) && (
        <button
          type="button" onClick={() => onChange({ from: "", to: "" })}
          data-testid={`${testIdPrefix}-clear`}
          className="text-[11px] text-blue-600 hover:underline font-medium"
        >Clear</button>
      )}
    </div>
  );
}

// Mar 2 2026 — top-bar preset picker. Emits an {from,to} range whenever the
// user picks a preset or fills both custom dates. Presets are computed
// against the *local* wall-clock so "This Month" behaves how ops expect
// regardless of server tz.
const PRESET_OPTIONS = [
  { value: "", label: "Date range: All time" },
  { value: "this_month", label: "This Month" },
  { value: "last_month", label: "Last Month" },
  { value: "last_3_months", label: "Last 3 Months" },
  { value: "this_year", label: "This Year" },
  { value: "last_year", label: "Last Year" },
  { value: "custom", label: "Custom…" },
];

function _toISO(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
}

function computePreset(preset) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (preset === "this_month") {
    const from = new Date(today.getFullYear(), today.getMonth(), 1);
    return { from: _toISO(from), to: _toISO(today) };
  }
  if (preset === "last_month") {
    const from = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    const to   = new Date(today.getFullYear(), today.getMonth(), 0);   // day 0 = last day prev month
    return { from: _toISO(from), to: _toISO(to) };
  }
  if (preset === "last_3_months") {
    const from = new Date(today.getFullYear(), today.getMonth() - 3, today.getDate());
    return { from: _toISO(from), to: _toISO(today) };
  }
  if (preset === "this_year") {
    const from = new Date(today.getFullYear(), 0, 1);
    const to   = new Date(today.getFullYear(), 11, 31);
    return { from: _toISO(from), to: _toISO(to) };
  }
  if (preset === "last_year") {
    const y = today.getFullYear() - 1;
    return { from: `${y}-01-01`, to: `${y}-12-31` };
  }
  return { from: "", to: "" };
}

function DateRangePresetPicker({ value, onApply, testIdPrefix }) {
  // ``value`` is the preset key ("this_month", "custom", …). Custom dates
  // are local state so the user can pick them without every keystroke firing
  // an aggregation request — we apply only when both endpoints are set.
  const [preset, setPreset] = useState(value || "");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");

  const handlePresetChange = (next) => {
    setPreset(next);
    if (next === "" ) {
      setCustomFrom(""); setCustomTo("");
      onApply({ from: "", to: "" }, "");
      return;
    }
    if (next === "custom") {
      // Wait for the user to fill both dates before firing.
      return;
    }
    const r = computePreset(next);
    onApply(r, next);
  };

  const handleCustomChange = (from, to) => {
    setCustomFrom(from); setCustomTo(to);
    if (from && to) onApply({ from, to }, "custom");
  };

  return (
    <div className="inline-flex items-center gap-2">
      <select
        value={preset}
        onChange={(e) => handlePresetChange(e.target.value)}
        data-testid={`${testIdPrefix}-select`}
        className="h-8 px-3 text-[12px] bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-full border border-transparent focus:outline-none font-semibold"
      >
        {PRESET_OPTIONS.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
      {preset === "custom" && (
        <>
          <input
            type="date" value={customFrom}
            onChange={(e) => handleCustomChange(e.target.value, customTo)}
            data-testid={`${testIdPrefix}-custom-from`}
            className="h-8 px-2 border border-slate-200 rounded-md bg-white font-mono text-[12px] focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400"
          />
          <span className="text-slate-500 text-[11px]">to</span>
          <input
            type="date" value={customTo}
            onChange={(e) => handleCustomChange(customFrom, e.target.value)}
            data-testid={`${testIdPrefix}-custom-to`}
            className="h-8 px-2 border border-slate-200 rounded-md bg-white font-mono text-[12px] focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400"
          />
        </>
      )}
    </div>
  );
}

function SectionCard({ title, icon: Icon, iconColor = "#334155", children, right, testId }) {
  return (
    <section className="pt-2 pb-6 mb-2 border-b border-slate-200 last:border-b-0" data-testid={testId}>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <h2 className="text-[14px] font-extrabold uppercase tracking-[0.15em] text-slate-800 inline-flex items-center gap-2">
          {Icon && (
            <span
              className="w-7 h-7 rounded-full inline-flex items-center justify-center"
              style={{ background: `${iconColor}1A` }}
            >
              <Icon size={15} style={{ color: iconColor }} />
            </span>
          )}
          {title}
        </h2>
        <div className="flex items-center gap-2">{right}</div>
      </div>
      {children}
    </section>
  );
}

/**
 * StatCard — accent-tinted card with:
 *   • 10%-opacity accent tint background
 *   • 4px accent left border
 *   • uppercase accent-coloured label
 *   • 2.5rem slate primary number
 *   • muted-gray amount
 *   • accent circle icon top-right
 *   • accent-tinted sparkline at bottom
 *   • hover elevation
 */
function StatCard({ label, bundle, accent = "#64748B", icon: Icon, loading, compare, testId }) {
  const tintBg = `${accent}1A`;   // 10% opacity
  const circleBg = `${accent}26`; // ~15% opacity
  if (loading) {
    return (
      <div
        className="rounded-lg p-4 animate-pulse"
        style={{ background: tintBg, borderLeft: `4px solid ${accent}` }}
        data-testid={`${testId}-skeleton`}
      >
        <div className="h-3 w-24 bg-white/60 rounded mb-3" />
        <div className="h-10 w-20 bg-white/60 rounded mb-2" />
        <div className="h-4 w-24 bg-white/40 rounded mb-2" />
        <div className="h-7 w-full bg-white/40 rounded" />
      </div>
    );
  }
  const b = bundle || {};
  return (
    <div
      className="rounded-lg p-4 transition-all duration-200 hover:shadow-md hover:-translate-y-[1px] cursor-default"
      style={{ background: tintBg, borderLeft: `4px solid ${accent}` }}
      data-testid={testId}
    >
      <div className="flex items-start justify-between mb-2">
        <span
          className="text-[10.5px] uppercase tracking-[0.12em] font-bold"
          style={{ color: accent }}
        >
          {label}
        </span>
        {Icon && (
          <span
            className="w-8 h-8 rounded-full inline-flex items-center justify-center shrink-0"
            style={{ background: circleBg }}
          >
            <Icon size={15} style={{ color: accent }} />
          </span>
        )}
      </div>
      <div className="font-bold text-slate-800 leading-none tracking-tight" style={{ fontSize: "2.5rem" }}>
        {fmtCount(b.count)}
      </div>
      <div className="text-[13px] text-slate-500 mt-2 font-medium">{fmtAmt(b.amount)}</div>
      {compare && (
        <div className="mt-1.5">
          <DeltaBadge cur={b.count} prev={b.prev_count} testId={`${testId}-delta`} />
        </div>
      )}
      <Sparkline data={b.spark} color={accent} testId={`${testId}-spark`} />
    </div>
  );
}

function PctCard({ label, percent, prevPercent, compare, loading, testId }) {
  const accent = "#8B5CF6"; // violet
  const tintBg = `${accent}1A`;
  const circleBg = `${accent}26`;
  if (loading) {
    return (
      <div
        className="rounded-lg p-4 animate-pulse"
        style={{ background: tintBg, borderLeft: `4px solid ${accent}` }}
        data-testid={`${testId}-skeleton`}
      >
        <div className="h-3 w-16 bg-white/60 rounded mb-3" />
        <div className="h-10 w-24 bg-white/60 rounded" />
      </div>
    );
  }
  return (
    <div
      className="rounded-lg p-4 transition-all duration-200 hover:shadow-md hover:-translate-y-[1px] cursor-default flex flex-col justify-center"
      style={{ background: tintBg, borderLeft: `4px solid ${accent}` }}
      data-testid={testId}
    >
      <div className="flex items-start justify-between mb-2">
        <span
          className="text-[10.5px] uppercase tracking-[0.12em] font-bold"
          style={{ color: accent }}
        >{label}</span>
        <span
          className="w-8 h-8 rounded-full inline-flex items-center justify-center shrink-0"
          style={{ background: circleBg }}
        >
          <TrendingUp size={15} style={{ color: accent }} />
        </span>
      </div>
      <div className="font-bold leading-none tracking-tight" style={{ fontSize: "2.5rem", color: accent }}>
        {Number(percent || 0).toFixed(2)}%
      </div>
      {compare && (
        <div className="mt-2">
          <DeltaBadge cur={percent} prev={prevPercent} testId={`${testId}-delta`} />
        </div>
      )}
    </div>
  );
}

function TableSkeleton({ cols = 7, rows = 5 }) {
  return (
    <div className="animate-pulse space-y-1" data-testid="dashboard-table-skeleton">
      {Array.from({ length: rows }).map((_, r) => (
        <div key={r} className="flex gap-2">
          {Array.from({ length: cols }).map((__, c) => (
            <div key={c} className={`h-8 bg-slate-100 rounded ${c === 0 ? "w-10" : "flex-1"}`} />
          ))}
        </div>
      ))}
    </div>
  );
}

// ── per-section hook ────────────────────────────────────────────────────────
function useSection(path, params) {
  const [state, setState] = useState({ data: null, loading: true, error: null });
  const key = JSON.stringify(params);
  useEffect(() => {
    let cancelled = false;
    setState((s) => ({ ...s, loading: true }));
    api.get(path, { params })
      .then((r) => { if (!cancelled) setState({ data: r.data, loading: false, error: null }); })
      .catch((e) => { if (!cancelled) setState({ data: null, loading: false, error: formatApiError(e) }); });
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, [path, key]);
  return state;
}

// ── main page ───────────────────────────────────────────────────────────────
export default function Dashboard() {
  // Global filters — apply to every section
  const [filterAccounts, setFilterAccounts] = useState([]);
  const [filterMarketplaces, setFilterMarketplaces] = useState([]);
  // Mar 2 2026 — top-bar Warehouse filter (multi-select). Resolves to
  // account_ids server-side via the accounts collection, exactly like the
  // Orders/Procurement top bar.
  const [filterWarehouses, setFilterWarehouses] = useState([]);

  // Comparison mode toggle — applies to all sections
  const [compare, setCompare] = useState(false);

  // Per-section date ranges (independent)
  const [ordersRange,   setOrdersRange]   = useState({ from: "", to: "" });
  const [claimsRange,   setClaimsRange]   = useState({ from: "", to: "" });
  const [shippingRange, setShippingRange] = useState({ from: "", to: "" });
  const [rtoRange,      setRtoRange]      = useState({ from: "", to: "" });
  const [returnsRange,  setReturnsRange]  = useState({ from: "", to: "" });
  const [productsRange, setProductsRange] = useState({ from: "", to: "" });
  const [identRange,    setIdentRange]    = useState({ from: "", to: "" });

  // Section 7 has its own extra marketplace filter (per spec)
  const [identMarketplaces, setIdentMarketplaces] = useState([]);

  // Filter options (accounts + marketplaces + warehouses available to this user)
  const [options, setOptions] = useState({ accounts: [], marketplaces: [], warehouses: [] });
  useEffect(() => {
    api.get("/dashboard/filters").then((r) => setOptions(r.data)).catch(() => {});
  }, []);

  // Mar 2 2026 — global date preset from the top bar. Whenever the user
  // picks a preset (or a valid custom range), we cascade the resulting
  // range into every section's range state. If the user then edits any
  // individual section's date, only that section changes — the other
  // sections remain on the last cascaded value. Picking another preset
  // resets every section back to the new range (per spec).
  const applyGlobalRange = (range) => {
    setOrdersRange(range);
    setClaimsRange(range);
    setShippingRange(range);
    setRtoRange(range);
    setReturnsRange(range);
    setProductsRange(range);
    setIdentRange(range);
  };

  // Common param builder — folds in global filters + section date range
  const buildParams = (range, extra = {}) => {
    const p = { ...extra };
    if (range?.from) p.date_from = range.from;
    if (range?.to)   p.date_to   = range.to;
    if (filterAccounts.length)     p.account_id     = filterAccounts.join(",");
    if (filterMarketplaces.length) p.marketplace    = filterMarketplaces.join(",");
    if (filterWarehouses.length)   p.warehouse_name = filterWarehouses.join(",");
    return p;
  };

  // Individual section calls
  const s1 = useSection("/dashboard/orders",    useMemo(() => buildParams(ordersRange),   [ordersRange, filterAccounts, filterMarketplaces, filterWarehouses]));    // eslint-disable-line
  const s2 = useSection("/dashboard/claims",    useMemo(() => buildParams(claimsRange),   [claimsRange, filterAccounts, filterMarketplaces, filterWarehouses]));    // eslint-disable-line
  const s3 = useSection("/dashboard/shipping",  useMemo(() => buildParams(shippingRange), [shippingRange, filterAccounts, filterMarketplaces, filterWarehouses]));  // eslint-disable-line
  const s4 = useSection("/dashboard/rto",       useMemo(() => buildParams(rtoRange),      [rtoRange, filterAccounts, filterMarketplaces, filterWarehouses]));       // eslint-disable-line
  const s5 = useSection("/dashboard/returns",   useMemo(() => buildParams(returnsRange),  [returnsRange, filterAccounts, filterMarketplaces, filterWarehouses]));   // eslint-disable-line
  const s6 = useSection("/dashboard/top-products", useMemo(() => buildParams(productsRange), [productsRange, filterAccounts, filterMarketplaces, filterWarehouses])); // eslint-disable-line
  const s7 = useSection("/dashboard/top-identifiers", useMemo(() => {
    const p = buildParams(identRange);
    if (identMarketplaces.length) p.marketplace = identMarketplaces.join(",");
    return p;
  }, [identRange, filterAccounts, filterMarketplaces, filterWarehouses, identMarketplaces])); // eslint-disable-line

  const totalActiveFilters = filterAccounts.length + filterMarketplaces.length + filterWarehouses.length;

  // Mar 2 2026 — CSV download helpers for the two Top tables. Each honours
  // the current section date range + all top-bar globals, so users get a
  // full-dataset export that matches what they see on screen.
  const buildQuery = (range, extra = {}) => {
    const parts = [];
    const p = { ...buildParams(range, extra), format: "csv" };
    for (const [k, v] of Object.entries(p)) {
      if (v === undefined || v === null || v === "") continue;
      parts.push(`${encodeURIComponent(k)}=${encodeURIComponent(v)}`);
    }
    return parts.length ? `?${parts.join("&")}` : "";
  };
  const downloadTopProducts = async () => {
    try {
      const qs = buildQuery(productsRange);
      await downloadFile(`/dashboard/top-products${qs}`, "top_products.csv");
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const downloadTopIdentifiers = async () => {
    try {
      const extra = identMarketplaces.length ? { marketplace: identMarketplaces.join(",") } : {};
      const qs = buildQuery(identRange, extra);
      await downloadFile(`/dashboard/top-identifiers${qs}`, "top_marketplace_identifiers.csv");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // Accent palette — one hex per KPI card, per user spec (Feb 28 2026).
  const A = {
    // Orders
    ordersCancelled: "#F43F5E",
    ordersShipped:   "#6366F1",
    ordersRefunded:  "#F59E0B",
    ordersPending:   "#64748B",
    // Claims
    claimsRaised:     "#F97316",
    claimsAccepted:   "#10B981",
    claimsPending:    "#F59E0B",
    // Shipping
    shipShipped:   "#0EA5E9",
    shipDelivered: "#10B981",
    shipPending:   "#64748B",
    // RTO
    rtoReceived: "#F43F5E",
    rtoPending:  "#F59E0B",
    // Returns
    retReceived: "#F97316",
    retPending:  "#F59E0B",
    // Section header icons pick a neutral slate
    sectionIcon: "#334155",
  };

  // Shared pill styling for the global filter row (Feb 28 2026 restyle).
  const pillTrigger = "h-8 px-3 text-[12px] bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-full inline-flex items-center gap-2 min-w-[130px] justify-between border border-transparent focus:outline-none";

  return (
    <div className="bg-white min-h-screen">
      <PageHeader title="Dashboard" subtitle="Multi-marketplace operations at a glance" testId="dashboard-page-title">
        <MultiSelectFilter
          label="Account"
          options={options.accounts}
          value={filterAccounts}
          onChange={setFilterAccounts}
          testId="dashboard-filter-account"
          triggerClassName={pillTrigger}
        />
        <MultiSelectFilter
          label="Marketplace"
          options={options.marketplaces}
          value={filterMarketplaces}
          onChange={setFilterMarketplaces}
          testId="dashboard-filter-marketplace"
          triggerClassName={pillTrigger}
        />
        <MultiSelectFilter
          label="Warehouse"
          options={options.warehouses || []}
          value={filterWarehouses}
          onChange={setFilterWarehouses}
          testId="dashboard-filter-warehouse"
          triggerClassName={pillTrigger}
        />
        <DateRangePresetPicker
          value=""
          onApply={applyGlobalRange}
          testIdPrefix="dashboard-global-date"
        />
        <label
          className="inline-flex items-center gap-2 text-[12px] text-slate-700 select-none cursor-pointer bg-slate-100 hover:bg-slate-200 rounded-full px-3 h-8"
          data-testid="dashboard-compare-toggle-wrap"
          title="Show change vs the same-length prior period (needs a date range set)"
        >
          <input
            type="checkbox"
            checked={compare}
            onChange={(e) => setCompare(e.target.checked)}
            data-testid="dashboard-compare-toggle"
            className="accent-indigo-600"
          />
          <span className="font-semibold">vs previous period</span>
        </label>
        {totalActiveFilters > 0 && (
          <span className="text-[11px] text-slate-500 font-medium" data-testid="dashboard-active-count">
            {totalActiveFilters} filter{totalActiveFilters === 1 ? "" : "s"} applied globally
          </span>
        )}
      </PageHeader>
      <PageBody>
        {/* ── Section 1: Orders ─────────────────────────────────────────── */}
        <SectionCard
          title="Orders" icon={ShoppingCart} iconColor={A.sectionIcon} testId="dashboard-section-orders"
          right={<DateRange from={ordersRange.from} to={ordersRange.to} onChange={setOrdersRange} testIdPrefix="dashboard-orders-date" />}
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard label="Cancelled" bundle={s1.data?.cancelled} accent={A.ordersCancelled} icon={XCircle}       loading={s1.loading} compare={compare && s1.data?.has_prev} testId="stat-orders-cancelled" />
            <StatCard label="Shipped"   bundle={s1.data?.shipped}   accent={A.ordersShipped}   icon={Truck}         loading={s1.loading} compare={compare && s1.data?.has_prev} testId="stat-orders-shipped" />
            <StatCard label="Refunded"  bundle={s1.data?.refunded}  accent={A.ordersRefunded}  icon={RotateCcw}     loading={s1.loading} compare={compare && s1.data?.has_prev} testId="stat-orders-refunded" />
            <StatCard label="Pending"   bundle={s1.data?.pending}   accent={A.ordersPending}   icon={Package}       loading={s1.loading} compare={compare && s1.data?.has_prev} testId="stat-orders-pending" />
          </div>
        </SectionCard>

        {/* ── Section 2: Claims ─────────────────────────────────────────── */}
        {/* Mar 2 2026 — Claims now shows exactly 3 cards per spec:
              Raised   = orders with claim_status not blank
              Accepted = claim_status equals 'accepted' (case-insensitive)
              Pending  = raised minus accepted (any other non-blank value)  */}
        <SectionCard
          title="Claims" icon={FileText} iconColor={A.sectionIcon} testId="dashboard-section-claims"
          right={<DateRange from={claimsRange.from} to={claimsRange.to} onChange={setClaimsRange} testIdPrefix="dashboard-claims-date" />}
        >
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <StatCard label="Raised"     bundle={s2.data?.raised}     accent={A.claimsRaised}     icon={FileText}    loading={s2.loading} compare={compare && s2.data?.has_prev} testId="stat-claims-raised" />
            <StatCard label="Accepted"   bundle={s2.data?.accepted}   accent={A.claimsAccepted}   icon={CheckCircle2} loading={s2.loading} compare={compare && s2.data?.has_prev} testId="stat-claims-accepted" />
            <StatCard label="Pending"    bundle={s2.data?.pending}    accent={A.claimsPending}    icon={Package}     loading={s2.loading} compare={compare && s2.data?.has_prev} testId="stat-claims-pending" />
          </div>
        </SectionCard>

        {/* ── Section 3: Shipping ───────────────────────────────────────── */}
        <SectionCard
          title="Shipping" icon={Truck} iconColor={A.sectionIcon} testId="dashboard-section-shipping"
          right={<DateRange from={shippingRange.from} to={shippingRange.to} onChange={setShippingRange} testIdPrefix="dashboard-shipping-date" />}
        >
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <StatCard label="Shipped"   bundle={s3.data?.shipped}   accent={A.shipShipped}   icon={Truck}        loading={s3.loading} compare={compare && s3.data?.has_prev} testId="stat-shipping-shipped" />
            <StatCard label="Delivered" bundle={s3.data?.delivered} accent={A.shipDelivered} icon={CheckCircle2} loading={s3.loading} compare={compare && s3.data?.has_prev} testId="stat-shipping-delivered" />
            <StatCard label="Pending"   bundle={s3.data?.pending}   accent={A.shipPending}   icon={Package}      loading={s3.loading} compare={compare && s3.data?.has_prev} testId="stat-shipping-pending" />
          </div>
        </SectionCard>

        {/* ── Section 4: RTO ────────────────────────────────────────────── */}
        <SectionCard
          title="RTO" icon={ArrowLeftRight} iconColor={A.sectionIcon} testId="dashboard-section-rto"
          right={<DateRange from={rtoRange.from} to={rtoRange.to} onChange={setRtoRange} testIdPrefix="dashboard-rto-date" />}
        >
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <StatCard label="Received" bundle={s4.data?.received} accent={A.rtoReceived} icon={ArrowLeftRight} loading={s4.loading} compare={compare && s4.data?.has_prev} testId="stat-rto-received" />
            <StatCard label="Pending"  bundle={s4.data?.pending}  accent={A.rtoPending}  icon={Package}        loading={s4.loading} compare={compare && s4.data?.has_prev} testId="stat-rto-pending" />
            <PctCard  label="RTO %"    percent={s4.data?.rto_percent} prevPercent={s4.data?.prev_rto_percent} compare={compare && s4.data?.has_prev} loading={s4.loading} testId="stat-rto-percent" />
          </div>
        </SectionCard>

        {/* ── Section 5: Returns ────────────────────────────────────────── */}
        <SectionCard
          title="Returns" icon={Undo2} iconColor={A.sectionIcon} testId="dashboard-section-returns"
          right={<DateRange from={returnsRange.from} to={returnsRange.to} onChange={setReturnsRange} testIdPrefix="dashboard-returns-date" />}
        >
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <StatCard label="Received" bundle={s5.data?.received} accent={A.retReceived} icon={Undo2}    loading={s5.loading} compare={compare && s5.data?.has_prev} testId="stat-returns-received" />
            <StatCard label="Pending"  bundle={s5.data?.pending}  accent={A.retPending}  icon={Package}  loading={s5.loading} compare={compare && s5.data?.has_prev} testId="stat-returns-pending" />
            <PctCard  label="Return %" percent={s5.data?.return_percent} prevPercent={s5.data?.prev_return_percent} compare={compare && s5.data?.has_prev} loading={s5.loading} testId="stat-returns-percent" />
          </div>
        </SectionCard>

        {/* ── Section 6: Top Products ───────────────────────────────────── */}
        <SectionCard
          title="Top Products" icon={Boxes} iconColor={A.sectionIcon} testId="dashboard-section-top-products"
          right={(
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={downloadTopProducts}
                data-testid="dashboard-top-products-download"
                className="inline-flex items-center gap-1.5 h-8 px-3 text-[12px] bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-full font-semibold"
                title="Download every matching SKU as CSV (not just the top 10)"
              >
                <Download size={13} /> Download CSV
              </button>
              <DateRange from={productsRange.from} to={productsRange.to} onChange={setProductsRange} testIdPrefix="dashboard-products-date" />
            </div>
          )}
        >
          {s6.loading ? <TableSkeleton cols={7} /> : (
            <div className="overflow-x-auto">
              <table className="w-full text-[12.5px]" data-testid="dashboard-top-products-table">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50">
                    <th className="text-left px-2 py-1.5 font-semibold text-slate-600 uppercase text-[10.5px] tracking-wider">Image</th>
                    <th className="text-left px-2 py-1.5 font-semibold text-slate-600 uppercase text-[10.5px] tracking-wider">System SKU</th>
                    <th className="text-right px-2 py-1.5 font-semibold text-slate-600 uppercase text-[10.5px] tracking-wider">Total Orders</th>
                    <th className="text-right px-2 py-1.5 font-semibold text-slate-600 uppercase text-[10.5px] tracking-wider">Total Amount</th>
                    <th className="text-right px-2 py-1.5 font-semibold text-slate-600 uppercase text-[10.5px] tracking-wider">Shipped</th>
                    <th className="text-right px-2 py-1.5 font-semibold text-slate-600 uppercase text-[10.5px] tracking-wider">Refunded</th>
                    <th className="text-right px-2 py-1.5 font-semibold text-slate-600 uppercase text-[10.5px] tracking-wider">Pending</th>
                  </tr>
                </thead>
                <tbody>
                  {(s6.data?.rows || []).map((r, i) => (
                    <tr key={r.system_sku + i} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="px-2 py-1.5">
                        {r.image_url ? <ImageThumb src={r.image_url} size={64} testId={`dash-thumb-${r.system_sku || r.id}`} /> : "—"}
                      </td>
                      <td className="px-2 py-1.5 font-mono">{r.system_sku}</td>
                      <td className="px-2 py-1.5 text-right font-mono">{fmtCount(r.total_orders)}</td>
                      <td className="px-2 py-1.5 text-right font-mono">{fmtAmt(r.total_amount)}</td>
                      <td className="px-2 py-1.5 text-right font-mono text-sky-700">{fmtCount(r.shipped)}</td>
                      <td className="px-2 py-1.5 text-right font-mono text-amber-700">{fmtCount(r.refunded)}</td>
                      <td className="px-2 py-1.5 text-right font-mono text-slate-500">{fmtCount(r.pending)}</td>
                    </tr>
                  ))}
                  {!s6.data?.rows?.length && (
                    <tr><td colSpan={7} className="py-6 text-center text-slate-500">No orders match the current filters.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </SectionCard>

        {/* ── Section 7: Top Marketplace Identifiers ────────────────────── */}
        <SectionCard
          title="Top Marketplace Identifiers" icon={Barcode} iconColor={A.sectionIcon} testId="dashboard-section-top-identifiers"
          right={(
            <div className="flex items-center gap-2">
              <MultiSelectFilter
                label="Marketplace"
                options={options.marketplaces}
                value={identMarketplaces}
                onChange={setIdentMarketplaces}
                testId="dashboard-identifiers-marketplace"
                triggerClassName={pillTrigger}
              />
              <button
                type="button"
                onClick={downloadTopIdentifiers}
                data-testid="dashboard-top-identifiers-download"
                className="inline-flex items-center gap-1.5 h-8 px-3 text-[12px] bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-full font-semibold"
                title="Download every matching marketplace identifier as CSV (not just the top 10)"
              >
                <Download size={13} /> Download CSV
              </button>
              <DateRange from={identRange.from} to={identRange.to} onChange={setIdentRange} testIdPrefix="dashboard-identifiers-date" />
            </div>
          )}
        >
          {s7.loading ? <TableSkeleton cols={7} /> : (
            <div className="overflow-x-auto">
              <table className="w-full text-[12.5px]" data-testid="dashboard-top-identifiers-table">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50">
                    <th className="text-left px-2 py-1.5 font-semibold text-slate-600 uppercase text-[10.5px] tracking-wider">Image</th>
                    <th className="text-left px-2 py-1.5 font-semibold text-slate-600 uppercase text-[10.5px] tracking-wider">Identifier</th>
                    <th className="text-right px-2 py-1.5 font-semibold text-slate-600 uppercase text-[10.5px] tracking-wider">Total Orders</th>
                    <th className="text-right px-2 py-1.5 font-semibold text-slate-600 uppercase text-[10.5px] tracking-wider">Total Amount</th>
                    <th className="text-right px-2 py-1.5 font-semibold text-slate-600 uppercase text-[10.5px] tracking-wider">Shipped</th>
                    <th className="text-right px-2 py-1.5 font-semibold text-slate-600 uppercase text-[10.5px] tracking-wider">Refunded</th>
                    <th className="text-right px-2 py-1.5 font-semibold text-slate-600 uppercase text-[10.5px] tracking-wider">Pending</th>
                  </tr>
                </thead>
                <tbody>
                  {(s7.data?.rows || []).map((r, i) => (
                    <tr key={r.marketplace_identifier + i} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="px-2 py-1.5">
                        {r.image_url ? <ImageThumb src={r.image_url} size={64} testId={`dash-thumb-${r.system_sku || r.id}`} /> : "—"}
                      </td>
                      <td className="px-2 py-1.5 font-mono">{r.marketplace_identifier}</td>
                      <td className="px-2 py-1.5 text-right font-mono">{fmtCount(r.total_orders)}</td>
                      <td className="px-2 py-1.5 text-right font-mono">{fmtAmt(r.total_amount)}</td>
                      <td className="px-2 py-1.5 text-right font-mono text-sky-700">{fmtCount(r.shipped)}</td>
                      <td className="px-2 py-1.5 text-right font-mono text-amber-700">{fmtCount(r.refunded)}</td>
                      <td className="px-2 py-1.5 text-right font-mono text-slate-500">{fmtCount(r.pending)}</td>
                    </tr>
                  ))}
                  {!s7.data?.rows?.length && (
                    <tr><td colSpan={7} className="py-6 text-center text-slate-500">No orders match the current filters.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </SectionCard>
      </PageBody>
    </div>
  );
}
